import React from 'react'

export type ChartElementRef = HTMLElementTagNameMap['mtds-chart']

export type ChartDataRow = {
  label: string
  values: number[]
}

type PaddedChartColumns = {
  labels: string[]
  rows: ChartDataRow[]
  spacerIndexes: number[]
}

export const padChartColumns = ({
  labels,
  rows,
  minColumnCount = 5
}: {
  labels: string[]
  rows: ChartDataRow[]
  minColumnCount?: number
}): PaddedChartColumns => {
  if (labels.length >= minColumnCount) {
    return {
      labels,
      rows,
      spacerIndexes: []
    }
  }

  const spacerCount = minColumnCount - labels.length
  const leftSpacerCount = Math.floor(spacerCount / 2)
  const rightSpacerCount = spacerCount - leftSpacerCount
  const leftSpacers = Array.from({ length: leftSpacerCount })
  const rightSpacers = Array.from({ length: rightSpacerCount })

  return {
    labels: [
      ...leftSpacers.map(() => ''),
      ...labels,
      ...rightSpacers.map(() => '')
    ],
    rows: rows.map((row) => ({
      ...row,
      values: [
        ...leftSpacers.map(() => 0),
        ...row.values,
        ...rightSpacers.map(() => 0)
      ]
    })),
    spacerIndexes: [
      ...leftSpacers.map((_, index) => index),
      ...rightSpacers.map((_, index) => leftSpacerCount + labels.length + index)
    ]
  }
}

export const buildPaddedChartData = (
  { labels, rows }: PaddedChartColumns,
  label: string
): (string | number)[][] => [
  [label, ...labels],
  ...rows.map((row) => [row.label, ...row.values])
]

const hideSpacerBar = (bar: HTMLElement) => {
  bar.style.pointerEvents = 'none'
  bar.style.visibility = 'hidden'
  bar.removeAttribute('aria-label')
  bar.setAttribute('aria-hidden', 'true')
  bar.tabIndex = -1
}

const hideSpacerBarsForGroup = (group: HTMLElement | undefined) => {
  group?.querySelectorAll<HTMLElement>('.bar').forEach(hideSpacerBar)
}

const hideSpacerBars = (
  groups: NodeListOf<HTMLElement> | undefined,
  spacerIndexes: number[]
) => {
  spacerIndexes.forEach((index) => {
    hideSpacerBarsForGroup(groups?.[index])
  })
}

export const useHideChartSpacerBars = ({
  chartRef,
  spacerIndexes
}: {
  chartRef: React.RefObject<ChartElementRef | null>
  spacerIndexes: number[]
}) => {
  React.useEffect(() => {
    if (spacerIndexes.length === 0) return

    let observer: MutationObserver | null = null

    const updateSpacers = () => {
      const chart = chartRef.current
      const groups =
        chart?.shadowRoot?.querySelectorAll<HTMLElement>('.axisGroup')
      hideSpacerBars(groups, spacerIndexes)
    }

    const attachObserver = (): boolean => {
      const shadowRoot = chartRef.current?.shadowRoot
      if (observer || !shadowRoot) return false
      observer = new MutationObserver(updateSpacers)
      observer.observe(shadowRoot, { childList: true, subtree: true })
      return true
    }

    // The chart element can mount *after* this effect first runs (owner/plan
    // charts start empty until their category names resolve, so the chart is
    // conditionally rendered later). Read `chartRef.current` fresh on every tick
    // so a late-mounting element and its shadow root are still picked up, attach
    // the observer once available, then stop once the bars are present.
    let pollCount = 0
    const maxPolls = 100
    const tick = (): boolean => {
      attachObserver()
      updateSpacers()
      return Boolean(
        observer && chartRef.current?.shadowRoot?.querySelector('.bar')
      )
    }

    const pollId = window.setInterval(() => {
      pollCount += 1
      if (tick() || pollCount >= maxPolls) {
        window.clearInterval(pollId)
      }
    }, 100)

    tick()

    return () => {
      window.clearInterval(pollId)
      observer?.disconnect()
    }
  }, [chartRef, spacerIndexes])
}

const valueLabelsStyleId = 'chart-value-labels-style'
const valueLabelClassName = 'bar-value-label'

const addStyleToShadowRoot = (shadowRoot: ShadowRoot) => {
  if (shadowRoot.querySelector(`#${valueLabelsStyleId}`)) return

  const style = document.createElement('style')
  style.id = valueLabelsStyleId
  style.textContent = `
    .bar-value-label {
      position: absolute !important;
      left: 50% !important;
      top: -24px !important;
      transform: translateX(-50%) !important;
      font-size: 11px !important;
      font-weight: 600 !important;
      color: var(--mtds-color-text-default) !important;
      white-space: nowrap !important;
      pointer-events: none !important;
      z-index: 100 !important;
    }
    /* For horizontal stacked bar charts, show labels below each bar */
    :host([data-variant='bar-stacked']) .bar-value-label {
      top: 100% !important;
      left: 50% !important;
      transform: translateX(-50%) !important;
      margin-top: 4px !important;
      color: var(--mtds-color-text-default) !important;
      font-size: 10px !important;
    }
    .bar {
      position: relative !important;
    }
  `
  shadowRoot.appendChild(style)
}

const removeValueLabels = (shadowRoot: ShadowRoot | null) => {
  if (!shadowRoot) return

  shadowRoot.querySelector(`#${valueLabelsStyleId}`)?.remove()
  shadowRoot
    .querySelectorAll(`.${valueLabelClassName}`)
    .forEach((label) => label.remove())
}

const updateBarLabel = (bar: HTMLElement, value: number) => {
  let label = bar.querySelector(`.${valueLabelClassName}`) as HTMLElement | null
  if (!label) {
    label = document.createElement('span')
    label.className = valueLabelClassName
    label.setAttribute('aria-hidden', 'true')
    bar.appendChild(label)
  }

  const nextText = value.toLocaleString('nb-NO')
  if (label.textContent !== nextText) {
    label.textContent = nextText
  }
  const nextDisplay = value === 0 ? 'none' : 'block'
  if (label.style.display !== nextDisplay) {
    label.style.display = nextDisplay
  }
}

const addValueLabelsToBars = (
  shadowRoot: ShadowRoot | null,
  rows: Array<{ values: number[] }>,
  spacerIndexes: number[]
) => {
  if (!shadowRoot) return

  addStyleToShadowRoot(shadowRoot)

  const groups = shadowRoot.querySelectorAll<HTMLElement>('.axisGroup')
  if (groups.length === 0) return

  groups.forEach((group, columnIndex) => {
    if (spacerIndexes.includes(columnIndex)) return

    group.querySelectorAll<HTMLElement>('.bar').forEach((bar, rowIndex) => {
      updateBarLabel(bar, rows[rowIndex]?.values[columnIndex] ?? 0)
    })
  })
}

export const useChartBarValueLabels = ({
  chartRef,
  rows,
  spacerIndexes = []
}: {
  chartRef: React.RefObject<ChartElementRef | null>
  rows: Array<{ label: string; values: number[] }>
  spacerIndexes?: number[]
}) => {
  React.useEffect(() => {
    let observer: MutationObserver | null = null
    let observedShadowRoot: ShadowRoot | null = null

    const updateLabels = () => {
      addValueLabelsToBars(
        chartRef.current?.shadowRoot ?? null,
        rows,
        spacerIndexes
      )
    }

    // Once we observe the shadow root we can re-apply labels after every chart
    // rebuild (safe because re-applying is a no-op).
    const attachObserver = (): boolean => {
      const shadowRoot = chartRef.current?.shadowRoot
      if (observer || !shadowRoot) return false
      observedShadowRoot = shadowRoot
      observer = new MutationObserver(updateLabels)
      observer.observe(shadowRoot, { childList: true, subtree: true })
      return true
    }

    // The chart *element* can mount after this effect first runs: owner/plan
    // charts start empty until their category names resolve, so they are
    // conditionally rendered later. Capturing `chartRef.current` once would then
    // read null forever (that was the bug that left these two charts without
    // labels). Read the ref fresh on every tick so a late-mounting element and
    // its shadow root are picked up, attach the observer as soon as it exists,
    // then stop once the bars are present (the observer keeps labels in sync
    // from there). Cap the polling so it can't run forever.
    let pollCount = 0
    const maxPolls = 100
    const tick = (): boolean => {
      attachObserver()
      updateLabels()
      return Boolean(
        observer && chartRef.current?.shadowRoot?.querySelector('.bar')
      )
    }

    const pollId = window.setInterval(() => {
      pollCount += 1
      if (tick() || pollCount >= maxPolls) {
        window.clearInterval(pollId)
      }
    }, 100)

    tick()

    return () => {
      window.clearInterval(pollId)
      observer?.disconnect()
      removeValueLabels(observedShadowRoot)
    }
  }, [chartRef, rows, spacerIndexes])
}
